import React, {useState, useEffect, useMemo} from 'react';
import {Box, Checkbox, Grid, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {ReactComponent as CheckedIcon} from '../../../../../src/assets/images/icon-checked.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../../src/assets/images/icon-unchecked.svg';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ArrowDropUp, InfoOutline} from '@mui/icons-material';
import {ArrowDropDownIcon} from '@mui/x-date-pickers';
import {ISelectItem} from '../../../../shared/types';
import {RoleId, rolesList} from '../../../../shared/constants';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrModal from '../../../common/arr-modal/ArrModal';
import './DetailsTab.scss';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrMultiSelectDropdown from '../../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import {IProjectItem, SecurityGroupItem} from '../../../../api/types/user.type';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';

type NameTouched = {
  fName: boolean;
  lName: boolean;
};
type FieldErrors = Partial<{
  FirstName: string;
  LastName: string;
}> &
  Record<string, string | undefined>;
type SelectItemForDropdown = ISelectItem & {
  key: number | string;
  value: string | number;
};
const sgToSelect = (sg: SecurityGroupItem): SelectItemForDropdown => ({
  key: sg.key,
  value: sg.value ?? sg.text ?? String(sg.key),
  label: sg.value ?? sg.text ?? String(sg.key), // safe default if component reads label
  // keep extra fields if useful for debugging or future use
  isSelected: sg.isSelected,
  text: sg.text,
});

// 🔁 Map ISelectItem[] -> SecurityGroupItem[] by looking up from the options
const selectListToSgList = (
  selected: ISelectItem[],
  allOptions: SecurityGroupItem[]
): SecurityGroupItem[] => {
  const byKey = new Map(allOptions.map((opt) => [String(opt.key), opt]));
  return selected
    .map((sel) => byKey.get(String(sel.key))) // relies on dropdownItemKey="key"
    .filter((x): x is SecurityGroupItem => Boolean(x));
};
const MAX_NAME_LENGTH = 50;
interface DetailsTabProps {
  editUserRole: string;
  rolesOptions: ISelectItem[];
  firstName: string;
  lastName: string;
  primaryEmail: string;
  selectedRole: ISelectItem | null;
  isBillableUser: boolean;
  isAttorneyProfile: boolean;
  isActive: boolean;
  isLocked: boolean;
  securityGroups: SecurityGroupItem[];
  setFirstName: (val: string) => void;
  setLastName: (val: string) => void;
  setPrimaryEmail: (val: string) => void;
  setSelectedRole: (val: ISelectItem | null) => void;
  setIsBillableUser: (val: boolean) => void;
  setIsAttorneyProfile: (val: boolean) => void;
  setIsActive: (val: boolean) => void;
  setIsLocked: (val: boolean) => void;
  setSecurityGroups: (val: SecurityGroupItem[]) => void;
  securityGRPsOptions: SecurityGroupItem[];
  domainName?: string;
  projectName?: string | IProjectItem;
  projectOptions?: ISelectItem[];
  project?: ISelectItem | null;
  setProject?: (val: ISelectItem | null) => void;
  isFNameValid: boolean;
  isFNameMaxLengthValid: boolean;
  isLNameValid: boolean;
  isLNameMaxLengthValid: boolean;
  touchedFields: NameTouched;
  fieldErrors: FieldErrors;
  handleBlur: (field: 'fName' | 'lName') => void;
}

const DetailsTab: React.FC<DetailsTabProps> = ({
  editUserRole,
  rolesOptions,
  firstName,
  lastName,
  primaryEmail,
  selectedRole,
  isBillableUser,
  isAttorneyProfile,
  isActive,
  isLocked,
  securityGroups,
  setFirstName,
  setLastName,
  setPrimaryEmail,
  setSelectedRole,
  setIsBillableUser,
  setIsAttorneyProfile,
  setIsActive,
  setIsLocked,
  setSecurityGroups,
  securityGRPsOptions,
  domainName,
  projectName,
  projectOptions,
  project,
  setProject,
  isFNameValid,
  isFNameMaxLengthValid,
  isLNameValid,
  isLNameMaxLengthValid,
  touchedFields,
  fieldErrors,
  handleBlur,
}) => {
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const [isOpenRoleDef, setIsOpenRoleDef] = useState<boolean>(false);
  const [displayedRole, setDisplayedRole] = useState<ISelectItem | null>(
    selectedRole
  );
  const [pendingRole, setPendingRole] = useState<ISelectItem | null>(null);
  const [roleChangesModal, setRoleChangesModal] = useState<boolean>(false);
  const [displayedActive, setDisplayedActive] = useState<boolean>(isActive);
  const [activeChangeModal, setActiveChangeModal] = useState<boolean>(false);

  const toggleRoleDef = () => setIsOpenRoleDef((prev) => !prev);

  useEffect(() => {
    setDisplayedRole(selectedRole);
  }, [selectedRole]);

  useEffect(() => {
    setDisplayedActive(isActive);
  }, [isActive]);

  const handleRoleChange = (selected: ISelectItem | null) => {
    if (selectedRole?.key !== selected?.key) {
      setPendingRole(selected);
      setDisplayedRole(selected);
      setRoleChangesModal(true);
    }
  };

  const handleRoleChangeCancel = () => {
    setDisplayedRole(selectedRole);
    setPendingRole(null);
    setRoleChangesModal(false);
  };

  const handleRoleChangeConfirm = () => {
    if (pendingRole) {
      setSelectedRole(pendingRole);
    }
    setPendingRole(null);
    setRoleChangesModal(false);
  };

  const handleActiveChange = (value: boolean) => {
    setDisplayedActive(value);
    if (isActive != value && !value) {
      setActiveChangeModal(true);
    } else {
      setIsActive(value);
    }
  };

  const handleActiveCancel = () => {
    setIsActive(true);
    setDisplayedActive(true);
    setActiveChangeModal(false);
  };

  const handleActiveChangeConfirm = () => {
    setIsActive(false);
    setDisplayedActive(false);
    setActiveChangeModal(false);
  };
  const selectedRoleKey = selectedRole?.key?.toString();
  const isRMU = String(loginUserData.roleId) === RoleId.ReviewManager;
  const isSecurityGroups =
    selectedRoleKey === RoleId.ReviewManager ||
    selectedRoleKey === RoleId.Reviewer ||
    ((editUserRole == RoleId.ReviewManager ||
      editUserRole == RoleId.Reviewer) &&
      (selectedRoleKey == RoleId.ReviewManager ||
        selectedRoleKey == RoleId.Reviewer));

  const dropdownSecurityGrpItems: SelectItemForDropdown[] = useMemo(
    () => securityGRPsOptions.map(sgToSelect),
    [securityGRPsOptions]
  );

  const selectedSecurityGrpItems: SelectItemForDropdown[] = useMemo(
    () => securityGroups.map(sgToSelect),
    [securityGroups]
  );

  return (
    <Box className="details-tab-content">
      <Grid container columnSpacing={4} rowSpacing={3}>
        {/* First Name */}
        <Grid size={{xs: 12, sm: 6}}>
          <ArrTextField
            id="first-name-input"
            dataTestId="details-tab-sfirst-name-input"
            state={
              !isFNameValid ||
              fieldErrors.FirstName ||
              !isFNameMaxLengthValid ||
              (touchedFields.fName && !firstName)
                ? 'error'
                : 'enabled'
            }
            label={t('userManagement.addUser.fName')}
            required
            value={firstName}
            aria-label={t('userManagement.addUser.fName')}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => handleBlur('fName')}
            supportText={(() => {
              if (fieldErrors.FirstName) return fieldErrors.FirstName;
              if (touchedFields.fName && !firstName)
                return t('userManagement.addUser.fnameRequired');
              if (!isFNameValid && firstName)
                return t('userManagement.addUser.nameValidationMsg');
              if (!isFNameMaxLengthValid)
                return t('userManagement.addUser.maxLengthMsg', {
                  max: MAX_NAME_LENGTH,
                });
              return '';
            })()}
            showErrorIcon={
              !!fieldErrors.FirstName ||
              !isFNameValid ||
              !isFNameMaxLengthValid ||
              (touchedFields.fName && !firstName)
            }
            aria-invalid={
              !!fieldErrors.FirstName ||
              !isFNameValid ||
              !isFNameMaxLengthValid ||
              (touchedFields.fName && !firstName)
            }
          />
        </Grid>

        {/* Last Name */}
        <Grid size={{xs: 12, sm: 6}}>
          <ArrTextField
            id="last-name-input"
            dataTestId="details-tab-last-name-input"
            state={
              !!fieldErrors.LastName ||
              !isLNameValid ||
              !isLNameMaxLengthValid ||
              (touchedFields.lName && !lastName)
                ? 'error'
                : 'enabled'
            }
            label={t('userManagement.addUser.lName')}
            required
            aria-label={t('userManagement.addUser.lName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onBlur={() => handleBlur('lName')}
            supportText={(() => {
              if (fieldErrors.LastName) return fieldErrors.LastName;
              if (touchedFields.lName && !lastName)
                return t('userManagement.addUser.lnameRequired');
              if (!isLNameValid && lastName)
                return t('userManagement.addUser.nameValidationMsg');
              if (!isLNameMaxLengthValid)
                return t('userManagement.addUser.maxLengthMsg', {
                  max: MAX_NAME_LENGTH,
                });
              return '';
            })()}
            showErrorIcon={
              !!fieldErrors.LastName ||
              !isLNameValid ||
              !isLNameMaxLengthValid ||
              (touchedFields.lName && !lastName)
            }
            aria-invalid={
              !!fieldErrors.LastName ||
              !isLNameValid ||
              !isLNameMaxLengthValid ||
              (touchedFields.lName && !lastName)
            }
          />
        </Grid>

        {/* Primary Email */}
        <Grid size={{xs: 12, sm: 12}}>
          <ArrTextField
            id="email-input"
            dataTestId="details-tab-email-input"
            state="inactive"
            label={t('userManagement.addUser.primaryEmail')}
            required
            value={primaryEmail}
            onChange={(e) => setPrimaryEmail(e.target.value)}
          />
        </Grid>

        {/* Billable User */}
        {selectedRoleKey && selectedRoleKey != RoleId.SystemAdministrator && (
          <Grid size={{xs: 12, sm: 12}}>
            <Box className="check-box-input">
              <Checkbox
                data-testid="is-billable-checkbox"
                className="add-user-checkbox"
                checked={isBillableUser}
                size="small"
                icon={<UnCheckedIcon />}
                checkedIcon={<CheckedIcon />}
                onChange={(e) => setIsBillableUser(e.target.checked)}
                inputProps={{'data-testid': 'is-billable-checkbox'}}
              />
              <Box className="checkbox-label">
                <Typography
                  className="typo-text body3_size label"
                  data-testid={`details-tab-billable-user`}
                >
                  {t('userManagement.addUser.billableUser')}&nbsp;
                </Typography>
                <ArrTooltipWrapper
                  trigger={
                    <InfoOutline
                      fontSize="small"
                      className="billable-user-info-icon"
                    />
                  }
                >
                  {t('userManagement.addUser.billableUserInfo')}
                </ArrTooltipWrapper>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Role Dropdown */}
        <Grid size={{xs: 12, sm: 12}}>
          <ArrSingleSelectDropdown
            dropdownLabel={t('userManagement.addUser.selectRole')}
            dropdownItems={rolesOptions}
            selectedValues={displayedRole}
            dropdownItemKey="key"
            dropdownItemDisplay="value"
            onSelectionChange={handleRoleChange}
            dataTestId="user-select-role"
            disabled={editUserRole === RoleId.SystemAdministrator}
          />
        </Grid>

        {/* Attorney Profile */}
        {((editUserRole === RoleId.ReviewManager &&
          selectedRoleKey === RoleId.ReviewManager) ||
          selectedRoleKey === RoleId.ReviewManager) && (
          <Grid size={{xs: 12, sm: 12}}>
            <Box className="check-box-input">
              <Checkbox
                data-testid="is-attorney-checkbox"
                className="add-user-checkbox"
                checked={isAttorneyProfile}
                size="small"
                icon={<UnCheckedIcon />}
                checkedIcon={<CheckedIcon />}
                onChange={(e) => setIsAttorneyProfile(e.target.checked)}
                inputProps={{'data-testid': 'is-attorney-checkbox'}}
              />
              <Box className="checkbox-label">
                <Typography
                  className="typo-text body3_size label"
                  data-testid={`details-tab-attorney-profile`}
                >
                  {t('userManagement.addUser.attorneyProfile')}&nbsp;
                </Typography>
                <ArrTooltipWrapper
                  trigger={
                    <InfoOutline
                      fontSize="small"
                      className="billable-user-info-icon"
                    />
                  }
                >
                  {t('userManagement.addUser.attorneyProfileInfo')}
                </ArrTooltipWrapper>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Role Definition */}
        <Grid size={{xs: 12, sm: 12}}>
          <Box className="role-def-box">
            <ArrButton
              variant="text"
              size="xs"
              dataTestId="role-def"
              onClick={toggleRoleDef}
              startIcon={<InfoOutline />}
              endIcon={isOpenRoleDef ? <ArrowDropUp /> : <ArrowDropDownIcon />}
            >
              {t('userManagement.addUser.roleDef.title')}
            </ArrButton>
            {isOpenRoleDef && (
              <Box className="content">
                {rolesList.map((i, index) => (
                  <Box key={i}>
                    <Typography
                      component="span"
                      className="role-name"
                      data-testid={`details-tab-role-name-${index}`}
                    >
                      {`${t(`common.roleName.${i}`)}: `}
                    </Typography>
                    <Typography
                      component="span"
                      className="typo-text body5_size role-desc"
                      data-testid={`details-tab-typography-role-def-${index}`}
                    >
                      {t(`userManagement.addUser.roleDef.${i}`)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Grid>

        {/* Active / Locked */}
        <Grid size={{xs: 12, sm: 12}}>
          <Box className="check-box-input">
            <Checkbox
              data-testid="is-active-checkbox-span"
              className="add-user-checkbox"
              checked={displayedActive}
              size="small"
              icon={<UnCheckedIcon />}
              checkedIcon={<CheckedIcon />}
              onChange={(e) => handleActiveChange(e.target.checked)}
              inputProps={{'data-testid': 'is-active-checkbox'}}
            />
            <Box className="checkbox-label">
              <Typography
                className="typo-text body3_size label"
                data-testid={`details-tab-is-active`}
              >
                {t('userManagement.editUser.isActive')}
              </Typography>
            </Box>
          </Box>
          {!isRMU && (
            <Box className="check-box-input">
              <Checkbox
                data-testid="is-locked-checkbox-span"
                className="add-user-checkbox"
                checked={isLocked}
                size="small"
                icon={<UnCheckedIcon />}
                checkedIcon={<CheckedIcon />}
                onChange={(e) => setIsLocked(e.target.checked)}
                inputProps={{'data-testid': 'is-locked-checkbox'}}
              />
              <Box className="checkbox-label">
                <Typography
                  className="typo-text body3_size label"
                  data-testid={`details-tab-is-locked`}
                >
                  {t('userManagement.editUser.isLocked')}
                </Typography>
              </Box>
            </Box>
          )}
        </Grid>

        {/* Domain */}
        {editUserRole !== RoleId.SystemAdministrator &&
          domainName &&
          selectedRoleKey !== RoleId.SystemAdministrator && (
            <Grid size={{xs: 12, sm: 12}}>
              <ArrTextField
                state="inactive"
                label="Domain"
                dataTestId="details-tab-inactive-domain"
                value={domainName}
              />
            </Grid>
          )}

        {/* Project Name */}
        {(selectedRoleKey === RoleId.ProjectAdministrator ||
          selectedRoleKey === RoleId.ReviewManager ||
          selectedRoleKey === RoleId.Reviewer) &&
          projectName && (
            <Grid size={{xs: 12, sm: 12}}>
              <ArrTextField
                state="inactive"
                label="Project Name"
                value={projectName}
                dataTestId="details-tab-inactive-project-name"
              />
            </Grid>
          )}

        {/* Project Name */}
        {(selectedRoleKey === RoleId.ProjectAdministrator ||
          selectedRoleKey === RoleId.ReviewManager ||
          selectedRoleKey === RoleId.Reviewer) &&
          editUserRole !== RoleId.ProjectAdministrator &&
          editUserRole !== RoleId.ReviewManager &&
          editUserRole !== RoleId.Reviewer &&
          projectOptions && (
            <Grid size={{xs: 12, sm: 12}}>
              <ArrSingleSelectDropdown
                dropdownLabel={t('userManagement.addUser.selectProject')}
                dropdownItems={projectOptions}
                selectedValues={project ?? null}
                onSelectionChange={(value) => {
                  setSecurityGroups([]);
                  setProject?.(value);
                }}
                dropdownItemKey={'key'}
                dropdownItemDisplay={'value'}
                formControlRestProps={{required: true}}
                dataTestId="asd-details-tab-select-project"
                searchable
                showNoResultsText
              />
            </Grid>
          )}

        {/* Security Group */}
        {isSecurityGroups && (
          <Grid size={{xs: 12, sm: 12}}>
            <ArrMultiSelectDropdown
              dropdownLabel={t('userManagement.addUser.securityGroup')}
              dropdownItems={dropdownSecurityGrpItems}
              selectedValues={selectedSecurityGrpItems}
              onSelectionChange={(items) => {
                const mappedBack = selectListToSgList(
                  items,
                  securityGRPsOptions
                );
                setSecurityGroups(mappedBack);
              }}
              multiple
              searchable
              withControls
              dataTestId="select-security-grp"
              checkboxDataTestId="edit-user-details-security-group-dropdown-checkbox"
              dropdownItemKey="key"
              dropdownItemDisplay="value"
              formControlRestProps={{required: true}}
              onClearAll={() => {}}
              onApply={() => {}}
            />
          </Grid>
        )}
      </Grid>
      {/* Role Change Modal */}
      {roleChangesModal && (
        <ArrModal
          showModal={roleChangesModal}
          showClose={true}
          onClose={handleRoleChangeCancel}
          headerTitle={t('userManagement.editUser.roleChange.headerTitle')}
          title={t('userManagement.editUser.roleChange.title')}
          description={t('userManagement.editUser.roleChange.description')}
          dataTestId="role-change-modal"
          buttons={[
            {
              id: '1',
              label: t('userManagement.editUser.roleChange.cancel'),
              onClick: handleRoleChangeCancel,
              variant: 'secondary',
              dataTestId: 'rcm-cancel-button',
            },
            {
              id: '2',
              label: t('userManagement.editUser.roleChange.changeIt'),
              onClick: handleRoleChangeConfirm,
              variant: 'primary',
              dataTestId: 'rcm-confirm-button',
            },
          ]}
        />
      )}
      {/* Active Change Modal */}
      {activeChangeModal && (
        <ArrModal
          showModal={activeChangeModal}
          showClose={true}
          onClose={handleActiveCancel}
          headerTitle={t('userManagement.editUser.isActiveChange.headerTitle')}
          title={t('userManagement.editUser.isActiveChange.title')}
          description={t('userManagement.editUser.isActiveChange.description')}
          dataTestId="active-change-modal"
          buttons={[
            {
              id: '1',
              label: t('userManagement.editUser.isActiveChange.cancel'),
              onClick: handleActiveCancel,
              variant: 'secondary',
              dataTestId: 'acm-cancel',
            },
            {
              id: '2',
              label: t('userManagement.editUser.isActiveChange.changeIt'),
              onClick: handleActiveChangeConfirm,
              variant: 'primary',
              dataTestId: 'acm-confirm',
            },
          ]}
        />
      )}
    </Box>
  );
};

export default DetailsTab;
